import { useMemo, useState } from 'react';
import { Box, Button, Text } from '@backstage/ui';
import { SchemaFormField } from './schemaForm';
import { FormSection } from './formKit';
import type { JsonValue } from './schemaForm';
import { relevantFields } from './pipelineManifest';
import type { PipelineDescriptor } from './pipelineManifest';

/**
 * One pipeline's request form, laid out the way Control's manifest says.
 *
 * Shared by the two places a Control request is filled in:
 *
 *   - `ServiceOptionsField`, inside the service template, which hides the
 *     fields the template itself supplies (`exclude`);
 *   - `PipelineRequestPage`, `/create/templates/:pipelineId`, which renders the
 *     whole thing for a pipeline that needs no template at all.
 *
 * Nothing here knows any pipeline or field. Groups, order, labels, the
 * per-group and pipeline-wide Advanced disclosures and `showWhen` all come
 * from the manifest; a field placed nowhere falls into Advanced rather than
 * disappearing, so a field Control adds tomorrow is reachable the day it
 * ships.
 */
export function PipelineForm(props: {
  pipeline: PipelineDescriptor;
  /** What has been typed. Defaults are applied for display, never sent. */
  values: Record<string, JsonValue>;
  onSet: (name: string, value: JsonValue | undefined) => void;
  /** Fields a surrounding template supplies, never rendered here. */
  exclude?: ReadonlySet<string>;
  /** Values `ui.pattern` may interpolate. */
  context?: Record<string, string>;
  /** Per-field problems, shown under the field they name. */
  errors?: Record<string, string>;
  /**
   * Sections as titled blocks rather than cards — inside the scaffolder
   * wizard, whose step is already a card.
   */
  flat?: boolean;
}) {
  const { pipeline, values, onSet, exclude, context, errors, flat } = props;
  const [showAdvanced, setShowAdvanced] = useState(false);
  // Open groups' own advanced sections independently: expanding "more about
  // buckets" is a different act from expanding the pipeline-wide Advanced, and
  // one flag for both would open five disclosures at once.
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  const { grouped, advanced } = useMemo(() => {
    const properties = pipeline.schema.properties ?? {};
    const shown = Object.keys(properties).filter(k => !exclude?.has(k));
    const advancedSet = new Set(pipeline.ui.advanced ?? []);
    const placed = new Set<string>();

    const groups = (pipeline.ui.groups ?? []).map(group => {
      const take = (names: string[]) =>
        names.filter(f => {
          if (!shown.includes(f) || advancedSet.has(f) || placed.has(f)) {
            return false;
          }
          placed.add(f);
          return true;
        });
      // Order matters: plain fields claim their names before the group's own
      // advanced list does, so a field in both renders once, up front.
      const fields = take(group.fields);
      return { ...group, fields, advanced: take(group.advanced ?? []) };
    });

    // Anything grouped nowhere lands in Advanced, INCLUDING a field the
    // manifest's ui section has not caught up with. That is deliberate: a new
    // spec field must be reachable the moment Control ships it, without a
    // change here.
    return {
      grouped: groups,
      advanced: shown.filter(f => !placed.has(f)),
    };
  }, [pipeline, exclude]);

  // Transitive and default-aware: see relevantFields.
  const relevant = useMemo(
    () => relevantFields(pipeline, values),
    [pipeline, values],
  );

  /** One field, with whatever presentation Control published for it. */
  const renderField = (name: string) =>
    relevant.has(name) ? (
      <Box key={name}>
        <SchemaFormField
          name={name}
          schema={pipeline.schema.properties?.[name] ?? {}}
          ui={pipeline.ui.fields?.[name]}
          context={context}
          value={values[name]}
          onChange={v => onSet(name, v)}
        />
        {errors?.[name] && (
          <Text
            as="p"
            role="alert"
            variant="body-small"
            style={{
              color: 'var(--ar-err)',
              marginTop: 6,
              fontWeight: 560,
            }}
          >
            {errors[name]}
          </Text>
        )}
      </Box>
    ) : null;

  // A group whose every field is out of play (the AWS "Client data" group on
  // a GCP request) renders no heading either — an empty section titled
  // "Client data" reads as something the form forgot to ask.
  const visibleGroups = grouped.filter(g =>
    [...g.fields, ...g.advanced].some(f => relevant.has(f)),
  );
  // The disclosures themselves stay, with their full count, even when what is
  // behind them is not in play yet: "More about data (2)" says bucket lifetime
  // exists before anyone has asked for a bucket, and opening it shows the
  // question arrive as soon as they do.
  const visibleAdvanced = advanced;

  return (
    <>
      {visibleGroups.map((group, index) => {
        const groupAdvanced = group.advanced;
        return (
          <FormSection
            key={group.title}
            eyebrow={String(index + 1).padStart(2, '0')}
            title={group.title}
            hint={group.hint}
            flat={flat}
          >
            {group.fields.map(renderField)}
            {groupAdvanced.length > 0 && (
              <Box>
                <Button
                  variant="tertiary"
                  size="small"
                  onPress={() =>
                    setOpenGroups(o => ({
                      ...o,
                      [group.title]: !o[group.title],
                    }))
                  }
                  aria-expanded={Boolean(openGroups[group.title])}
                >
                  {openGroups[group.title] ? '▾' : '▸'} More about{' '}
                  {group.title.toLowerCase()} ({groupAdvanced.length})
                </Button>
                {openGroups[group.title] && (
                  <Box mt="3">{groupAdvanced.map(renderField)}</Box>
                )}
              </Box>
            )}
          </FormSection>
        );
      })}

      {visibleAdvanced.length > 0 && (
        <Box
          style={{
            border: '1px dashed var(--ar-border-strong)',
            borderRadius: 14,
            padding: showAdvanced ? '12px 18px 8px' : '8px 14px',
          }}
        >
          {/* A disclosure rather than an accordion component: the count in
              the label is the useful part — it says how much is hidden,
              which is what stops "Advanced" reading as "nothing here". */}
          <Button
            variant="tertiary"
            size="small"
            onPress={() => setShowAdvanced(v => !v)}
            aria-expanded={showAdvanced}
          >
            {showAdvanced ? '▾' : '▸'} Advanced ({visibleAdvanced.length})
          </Button>
          {showAdvanced && <Box mt="3">{visibleAdvanced.map(renderField)}</Box>}
        </Box>
      )}
    </>
  );
}
